'use client';

import { API_ROUTES, useApiInfiniteQuery } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { useScrollPaddingTop } from '@shared/utils';
import { useEffect, useMemo, useRef, type RefObject } from 'react';
import type { WalletTransactionsResponse } from '../../types';
import { OBSERVER_EXTRA_TOP_OFFSET_PX } from '../constants';
import { useFormattedDateLabel } from '../hooks/useFormattedDateLabel';
import { groupTransactionsByDay, monthKeyToDate, monthKeyToDateRange } from '../transactionDateHelpers';
import { useTransactionsContext } from '../TransactionsContext';
import type { MonthKey } from '../types';
import { DaySection } from './DaySection';
import { useScrollAnchor } from './hooks/useScrollAnchor';
import { useScrollToTarget } from './hooks/useScrollToTarget';
import { useSentinelFetch } from './hooks/useSentinelFetch';
import { MonthEmptyContent } from './MonthEmptyContent';
import { TransactionsLoadingSkeleton } from './TransactionsLoadingSkeleton';

const PAGE_SIZE = 100;
const LOADING_SKELETON_COUNT = 12;

type MonthSectionProps = {
  month: MonthKey;
  registerWithParentObserver: (el: Element) => () => void;
};

function MonthSection({ month, registerWithParentObserver }: MonthSectionProps) {
  const { namespace, monthIndexMap, lastTransactionMonthKey } = useTransactionsContext();
  const outerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    return registerWithParentObserver(el);
  }, [outerRef, registerWithParentObserver]);

  const lastTransactionMonthIndex = monthIndexMap.get(lastTransactionMonthKey) ?? -1;
  const monthIndex = monthIndexMap.get(month) ?? -1;
  const isKnownEmpty = monthIndex < lastTransactionMonthIndex;

  return (
    <div ref={outerRef} className="flex flex-col gap-6" data-month-key={month} data-testid={month}>
      {isKnownEmpty ? <MonthEmptyContent month={month} namespace={namespace} /> : <MonthSectionContent month={month} outerRef={outerRef} />}
    </div>
  );
}

type MonthSectionContentProps = {
  month: MonthKey;
  outerRef: RefObject<HTMLDivElement | null>;
};

function MonthSectionContent({ month, outerRef }: MonthSectionContentProps) {
  const {
    namespace,
    timeZone,
    isScrollLocked,
    scrollLockVersion,
    loadMonth,
    lastTransactionMonthKey,
    isFirstMonthLoaded,
    markFirstMonthLoaded,
    scrollTarget,
    setScrollTarget,
    isMonthActive,
  } = useTransactionsContext();
  const monthLabel = useFormattedDateLabel(namespace, monthKeyToDate(month), 'month_label');
  const stickyOffset = useScrollPaddingTop();
  const { session } = useSession();
  const playerId = session?.profile?.id;
  const path = API_ROUTES.MY_ACCOUNT.WALLET.GET_TRANSACTIONS.replace(':playerId', playerId ?? '');

  const [fromDate, toDate] = monthKeyToDateRange(month, timeZone);
  const enabled = month === lastTransactionMonthKey || isMonthActive(month);

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useApiInfiniteQuery<WalletTransactionsResponse, number>(path, {
    params: { from: fromDate, to: toDate, limit: PAGE_SIZE },
    credentials: true,
    pageParamKey: 'skip',
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => (lastPage?.moreAvailable ? allPages.length * PAGE_SIZE : undefined),
    queryOptions: {
      enabled: enabled && !!playerId,
      staleTime: 0,
    },
  });

  const transactions = useMemo(() => data?.pages.flatMap(page => page?.transactions ?? []) ?? [], [data]);
  const isLastTransactionMonth = month === lastTransactionMonthKey;

  const dayGroups = useMemo(() => groupTransactionsByDay(transactions, timeZone), [transactions, timeZone]);

  const sentinelRef = useRef<HTMLDivElement>(null);

  useScrollToTarget(outerRef, scrollTarget, month, setScrollTarget);

  useEffect(() => {
    if (isLastTransactionMonth && enabled && !isLoading) {
      markFirstMonthLoaded();
    }
  }, [isLastTransactionMonth, enabled, isLoading, markFirstMonthLoaded]);

  useEffect(() => {
    if (enabled || !isFirstMonthLoaded) return;
    const el = outerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          if (isScrollLocked()) return;
          loadMonth(month);
          observer.disconnect();
        }
      },
      { rootMargin: `-${stickyOffset + OBSERVER_EXTRA_TOP_OFFSET_PX}px 0px 200px 0px` }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled, isFirstMonthLoaded, month, loadMonth, isScrollLocked, scrollLockVersion, stickyOffset, outerRef]);

  useSentinelFetch(sentinelRef, hasNextPage, fetchNextPage, isScrollLocked, stickyOffset);
  useScrollAnchor(outerRef, stickyOffset);

  if (!enabled || isLoading) {
    const title = isLoading && isLastTransactionMonth ? undefined : monthLabel;
    return <TransactionsLoadingSkeleton title={title} skeletonCount={LOADING_SKELETON_COUNT} />;
  }

  if (transactions.length === 0) {
    return <MonthEmptyContent month={month} namespace={namespace} />;
  }

  return (
    <>
      {dayGroups.map(({ day, transactions: dayTransactions }) => (
        <DaySection key={day} day={day} transactions={dayTransactions} namespace={namespace} />
      ))}
      {hasNextPage && (
        <>
          <div ref={sentinelRef} />
          {isFetchingNextPage && <TransactionsLoadingSkeleton skeletonCount={LOADING_SKELETON_COUNT} />}
        </>
      )}
    </>
  );
}

export { MonthSection };
